/**
 * My profile in the portal: how each correctable topic reads, the form a
 * client fills in to correct it, and what gets sent. Values are keyed by
 * record field, the same as the server's correction topics.
 */
import type { Address } from "@/api/types/foreignNational";

import { parseDay } from "./portalModel";
import type { CorrectionTopic, PortalCorrection } from "./types";

export const TOPIC_FIELDS: Record<CorrectionTopic, string[]> = {
  legal_name: ["first_name", "middle_name", "last_name"],
  other_names: ["other_names"],
  birthday: ["birthday"],
  birthplace: ["city_of_birth", "state_of_birth", "country_of_birth"],
  citizenships: ["citizenships"],
  marital_status: ["marital_status"],
  email: ["email"],
  phone: ["phone"],
  physical_address: ["physical_address"],
  mailing_address: ["mailing_address"],
  history: []
};

export const ABOUT_TOPICS: CorrectionTopic[] = [
  "legal_name",
  "other_names",
  "birthday",
  "birthplace",
  "citizenships",
  "marital_status"
];

export const CONTACT_TOPICS: CorrectionTopic[] = [
  "email",
  "phone",
  "physical_address",
  "mailing_address"
];

export const MARITAL_STATUSES = [
  "single",
  "married",
  "divorced",
  "widowed",
  "separated",
  "annulled"
] as const;

export const ADDRESS_PARTS = [
  "street",
  "city",
  "state",
  "zip_code",
  "country"
] as const;

const LIST_FIELDS = new Set(["other_names", "citizenships"]);

export const isAddressTopic = (topic: CorrectionTopic): boolean =>
  topic === "physical_address" || topic === "mailing_address";

/** Form inputs for a topic, all strings. Address topics use `ADDRESS_PARTS`. */
export type CorrectionDraft = Record<string, string>;

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const list = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(text).filter(Boolean) : [];

const zip = (a: Address): string => text(a.zip_code ?? a.zipCode ?? a.zipcode);

export const formatAddress = (value: unknown): string => {
  if (!value || typeof value !== "object") return "";
  const a = value as Address;
  return [
    text(a.street),
    text(a.city),
    [text(a.state), zip(a)].filter(Boolean).join(" "),
    text(a.country)
  ]
    .filter(Boolean)
    .join(", ");
};

/** "March 14, 1988". Reads the date part only so it never shifts a day. */
export const formatLongDay = (value: unknown, language: string): string => {
  const date = parseDay(text(value).slice(0, 10));
  return date
    ? new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(date)
    : "";
};

export const pick = (
  topic: CorrectionTopic,
  source: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(TOPIC_FIELDS[topic].map((f) => [f, source[f]]));

/** How a topic's values read on the page ("" when nothing is on file). */
export const displayValue = (
  topic: CorrectionTopic,
  values: Record<string, unknown>,
  language: string,
  maritalLabel: (status: string) => string
): string => {
  switch (topic) {
    case "legal_name":
      return ["first_name", "middle_name", "last_name"]
        .map((f) => text(values[f]))
        .filter(Boolean)
        .join(" ");
    case "other_names":
    case "citizenships":
      return list(values[topic]).join(", ");
    case "birthday":
      return formatLongDay(values.birthday, language);
    case "birthplace":
      return TOPIC_FIELDS.birthplace
        .map((f) => text(values[f]))
        .filter(Boolean)
        .join(", ");
    case "marital_status":
      return text(values.marital_status)
        ? maritalLabel(text(values.marital_status))
        : "";
    case "physical_address":
    case "mailing_address":
      return formatAddress(values[topic]);
    case "history":
      return "";
    default:
      return text(values[topic]);
  }
};

/** One record field's value as text, for the firm's side-by-side review. */
export const fieldDisplay = (
  field: string,
  value: unknown,
  language: string
): string => {
  if (LIST_FIELDS.has(field)) return list(value).join(", ");
  if (field === "birthday") return formatLongDay(value, language);
  if (field.endsWith("_address")) return formatAddress(value);
  const s = text(value);
  return field === "marital_status" && s
    ? s.charAt(0).toUpperCase() + s.slice(1)
    : s;
};

export const draftFrom = (
  topic: CorrectionTopic,
  values: Record<string, unknown>
): CorrectionDraft => {
  if (isAddressTopic(topic)) {
    const a = (values[topic] ?? {}) as Address;
    return {
      street: text(a.street),
      city: text(a.city),
      state: text(a.state),
      zip_code: zip(a),
      country: text(a.country)
    };
  }
  return Object.fromEntries(
    TOPIC_FIELDS[topic].map((f) => {
      const value = values[f];
      if (LIST_FIELDS.has(f)) return [f, list(value).join(", ")];
      if (f === "birthday") return [f, text(value).slice(0, 10)];
      return [f, text(value)];
    })
  );
};

const comparable = (value: unknown): string => {
  if (Array.isArray(value)) return list(value).join("|").toLowerCase();
  if (value && typeof value === "object")
    return formatAddress(value).toLowerCase();
  const s = text(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s.toLowerCase();
};

/** The values to send: only fields that differ from what's on file. Empty
 * when nothing changed. */
export const proposedFrom = (
  topic: CorrectionTopic,
  draft: CorrectionDraft,
  current: Record<string, unknown>
): Record<string, unknown> => {
  const next: Record<string, unknown> = isAddressTopic(topic)
    ? {
        [topic]: Object.fromEntries(
          ADDRESS_PARTS.map((p) => [p, (draft[p] ?? "").trim()])
        )
      }
    : Object.fromEntries(
        TOPIC_FIELDS[topic].map((f) => [
          f,
          LIST_FIELDS.has(f)
            ? (draft[f] ?? "")
                .split(/[,\n]/)
                .map((s) => s.trim())
                .filter(Boolean)
            : (draft[f] ?? "").trim()
        ])
      );
  return Object.fromEntries(
    Object.entries(next).filter(
      ([f, v]) => comparable(v) !== comparable(current[f])
    )
  );
};

/** The pending suggestion for a topic, else its latest decision. */
export const correctionFor = (
  corrections: PortalCorrection[],
  topic: CorrectionTopic
): PortalCorrection | undefined =>
  corrections.find((c) => c.topic === topic && c.status === "pending") ??
  corrections.find((c) => c.topic === topic);

/** The reviewer's first name, for "Send to Andrew". */
export const reviewerName = (
  team: { name: string }[],
  fallback: string
): string => team[0]?.name.trim().split(/\s+/)[0] || fallback;

/** "Jun 2019 – now". */
export const historyPeriod = (
  row: { start: string | null; end: string | null; current: boolean },
  language: string,
  presentLabel: string
): string => {
  const month = (value: string | null) => {
    const date = parseDay(text(value).slice(0, 10));
    return date
      ? new Intl.DateTimeFormat(language, {
          month: "short",
          year: "numeric"
        }).format(date)
      : "";
  };
  const start = month(row.start);
  const end = row.current || !row.end ? presentLabel : month(row.end);
  if (start) return `${start} – ${end}`;
  return row.current || !row.end ? "" : end;
};
